import type { RoundAction } from "../../contracts/RoundSnapshot";
import type { ActionPhaseName } from "./ActionPhaseName";
import { CanonicalActionTypeNames } from "../../shared/CanonicalActionTypeNames";

/**
 * Resolves the action phase name for a RoundAction.
 *
 * Rule:
 * 1) If server provided actionPhaseName -> use it (authoritative)
 * 2) Otherwise map by canonical actionTypeName -> stable best-effort phase
 */
export function resolveActionPhaseName(roundAction: RoundAction): ActionPhaseName {
  // 1) server-authoritative override
  if (roundAction.actionPhaseName) {
    return roundAction.actionPhaseName;
  }

  // 2) best-effort mapping
  switch (roundAction.actionTypeName) {
    // --- frame_prepare: must happen before frame reveal/cascade animation
    case CanonicalActionTypeNames.spinBoundaryStart:
      return "frame_prepare";

    // You likely also want these in frame_prepare by default
    // because we usually want them applied before the frame reveals.
    case CanonicalActionTypeNames.presentVisibleMultipliers:
      return "frame_prepare";

    // --- win: the win-line highlight gets its OWN phase so a speed policy can KEEP it
    // (still present the win) while skipping the other pre-removal flourishes.
    // Game-defined actions that must always present (e.g. a win-multiplier fire, a
    // chance detonation) are NOT resolved here — their authoring (backend/fixtures)
    // MUST carry actionPhaseName: "win" explicitly (unknown types default to "other").
    case CanonicalActionTypeNames.gridWinHighlight:
      return "win";

    // --- pre_removal: show/mark before removing symbols
    // NOTE: phase does NOT control execution order among non-frame_prepare actions — the
    // engine runs them in authored order (see RoundReplayEngine).
    case CanonicalActionTypeNames.needleAward:
    case CanonicalActionTypeNames.symbolPopValue:
      return "pre_removal";

    // --- removal: mutation operations that clear symbols
    case CanonicalActionTypeNames.removeSymbolsByMask:
      return "removal";

    // --- post_removal: effects after removal/collapse
    case CanonicalActionTypeNames.collectGridMultipliers:
    case CanonicalActionTypeNames.addFeatureMultiplier:
    case CanonicalActionTypeNames.collectFeatureMultiplier:
    case CanonicalActionTypeNames.presentScatterState:
    case CanonicalActionTypeNames.bonusFeatureWon:
      return "post_removal";

    // --- settlement: end-of-spin summary and totals
    case CanonicalActionTypeNames.presentSpinWinMultiplication:
    case CanonicalActionTypeNames.updateTotalWinMeter:
    case CanonicalActionTypeNames.spinBoundaryEnd:
    case CanonicalActionTypeNames.presentBigWin:
      return "settlement";

    default:
      return "other";
  }
}